import { Stack, Typography } from "@mui/material";
import objectHash from "object-hash";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import adminApi from "../../../../api/adminApi";
import type { Contact, ContactWithFieldValues, SearchContactsRequest } from "../../../../api/types/fundStructureTypes";
import storage from "../../../../storage/storage";
import ExportWithFiltersButton from "../../../common/ExportWithFiltersButton";
import { FilterContextProvider } from "../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../common/filters/FiltersPanel";
import type { FilterState } from "../../../common/filters/filterState";
import ContactsGrid from "./ContactsGrid";
import ContactsJobDialog from "./ContactsJobDialog";
import DeleteContactDialog from "./DeleteContactDialog";
import SendInvitationDialog from "./SendInvitationDialog";
import { buildDeleteFinishNotification } from "./contactsJobNotifications";
import { getSelectOptionsForContacts } from "./filters/contactsSelectOptionsProvider";
import { getContactsFilterPayload } from "./filters/filterDefinitions";

const exportContactsToExcel = withErrorHandling(adminApi.exportContactsToExcel);

interface DialogState {
  contactIdsToInvite?: string[];
  contactIdsToDelete?: string[];
  contactToDelete?: Contact;
  openDialog: "invite_contacts" | "delete_contact" | "delete_contacts" | undefined;
}

interface Props {
  filterState: FilterState<ContactWithFieldValues>;
  setFilterState: Dispatch<SetStateAction<FilterState<ContactWithFieldValues>>>;
  notInvitedOnly?: boolean;
  excludedColumns?: string[];
  onContactsChanged?: () => void;
}

const ContactsTab = ({ filterState, setFilterState, notInvitedOnly, excludedColumns, onContactsChanged }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [dialogState, setDialogState] = useState<DialogState>({ openDialog: undefined });
  const [isExporting, setIsExporting] = useState(false);

  const payload = useMemo(
    () =>
      notInvitedOnly
        ? { ...getContactsFilterPayload(filterState), isNotInvited: true }
        : getContactsFilterPayload(filterState),
    [filterState, notInvitedOnly]
  );

  const payloadHash = objectHash(payload, { unorderedArrays: true });

  // eslint-disable-next-line @eslint-react/exhaustive-deps
  const getContacts = useCallback(() => adminApi.searchContacts(payload), [payloadHash]);

  const [contactsResp, contactsFetchError, { isFetching, fetch: fetchContacts }] = useFetch(getContacts);

  const handleExport = async (withFilters: boolean) => {
    setIsExporting(true);

    const exportPayload: SearchContactsRequest = withFilters
      ? payload
      : {
          fieldIds: [],
          includeFundInvestors: true,
          includeInboxData: true,
          ...(notInvitedOnly ? { isNotInvited: true } : {}),
        };
    const [fileDownloadInfo, error] = await exportContactsToExcel(exportPayload);

    setIsExporting(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[ContactsTab] exportContactsToExcel");
      return;
    }

    await downloadFileFromUrl(fileDownloadInfo.downloadUrl);
  };

  const handleInviteContacts = useCallback((contactIds: string[]) => {
    setDialogState({ openDialog: "invite_contacts", contactIdsToInvite: contactIds });
  }, []);

  const handleDeleteContacts = useCallback((contactIds: string[]) => {
    setDialogState({ openDialog: "delete_contacts", contactIdsToDelete: contactIds });
  }, []);

  const handleDeleteContact = useCallback((contact: Contact) => {
    setDialogState({ openDialog: "delete_contact", contactToDelete: contact });
  }, []);

  if (contactsFetchError) {
    logError(contactsFetchError, "[ContactsTab]");
    storage.clearFilterState(filterState.filterId);
    return <DataLoadingFailed title="Failed to load contacts" />;
  }

  const contacts = contactsResp?.items ?? [];
  const isLoading = isFetching || contactsResp === undefined;

  const handleCloseDialog = () => setDialogState({ openDialog: undefined });

  const handleContactsChanged = () => {
    setDialogState({ openDialog: undefined });
    fetchContacts();
    onContactsChanged?.();
  };

  return (
    <>
      <FilterContextProvider
        filterState={filterState}
        setFilterState={setFilterState}
        getSelectOptionsForFilter={getSelectOptionsForContacts}
      >
        <Stack spacing={2} sx={{ height: "100%" }}>
          <FiltersPanel totalCount={contactsResp?.total} recordCount={contacts.length} isLoading={isLoading}>
            <ExportWithFiltersButton
              disabled={isLoading || contacts.length === 0}
              loading={isExporting}
              onExportAllRecords={() => handleExport(false)}
              onExportFilteredRecords={() => handleExport(true)}
            />
          </FiltersPanel>

          <ContactsGrid
            allRows={contacts}
            excludedColumns={excludedColumns}
            isLoading={isLoading}
            onInviteContacts={handleInviteContacts}
            onDeleteContacts={handleDeleteContacts}
            onDeleteContact={handleDeleteContact}
          />
        </Stack>
      </FilterContextProvider>
      <SendInvitationDialog
        open={dialogState.openDialog === "invite_contacts"}
        onClose={handleCloseDialog}
        onInvitationSent={handleContactsChanged}
        contactIds={dialogState.contactIdsToInvite ?? []}
        description={
          <>
            You&apos;re about to send an invitation to <strong>{dialogState.contactIdsToInvite?.length ?? 0}</strong>{" "}
            {(dialogState.contactIdsToInvite?.length ?? 0) > 1 ? "contacts" : "contact"}.
          </>
        }
      />
      <ContactsJobDialog
        open={dialogState.openDialog === "delete_contacts"}
        onClose={handleCloseDialog}
        onSubmit={handleContactsChanged}
        contactIds={dialogState.contactIdsToDelete ?? []}
        title="Delete contacts"
        progressTitle="Deleting contact(s)..."
        actionLabel="Delete"
        cancelLabel="Cancel deletion"
        actionColor="error"
        actionVariant="outlined"
        startJob={adminApi.startDeleteContacts}
        buildFinishNotification={buildDeleteFinishNotification}
      >
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete contacts?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete <strong>{dialogState.contactIdsToDelete?.length ?? 0}</strong>{" "}
              {(dialogState.contactIdsToDelete?.length ?? 0) > 1 ? "contacts" : "contact"}. This action is permanent and
              can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </ContactsJobDialog>
      {dialogState.contactToDelete && (
        <DeleteContactDialog
          open={dialogState.openDialog === "delete_contact"}
          onClose={handleCloseDialog}
          onDeleted={handleContactsChanged}
          contact={dialogState.contactToDelete}
        />
      )}
    </>
  );
};

export default ContactsTab;
